import React, { useState, useRef, useEffect } from 'react';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import './DateRangePicker.css';

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function toDateOnly(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function fmtISO(d) {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fmtDisplay(d) {
  if (!d) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function buildMonthGrid(year, month) {
  // month is 0-indexed
  const first = new Date(year, month, 1);
  const startOffset = first.getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  return cells;
}

// value: { from: Date|null, to: Date|null }
// onChange: (from, to) => void  -- called when range is applied
const DateRangePicker = ({ value, onChange, label = 'Date Range' }) => {
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(value?.from || null);
  const [draftTo, setDraftTo] = useState(value?.to || null);
  const [hoverDate, setHoverDate] = useState(null);
  const [viewDate, setViewDate] = useState(value?.from || new Date());
  const containerRef = useRef(null);

  useEffect(() => {
    setDraftFrom(value?.from || null);
    setDraftTo(value?.to || null);
  }, [value?.from, value?.to]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handleDayClick = (day) => {
    if (!draftFrom || (draftFrom && draftTo)) {
      // Start a new selection
      setDraftFrom(day);
      setDraftTo(null);
      return;
    }
    // draftFrom set, draftTo not set yet
    if (day.getTime() < draftFrom.getTime()) {
      // Clicked before the start -> restart range from here
      setDraftFrom(day);
      setDraftTo(null);
    } else {
      setDraftTo(day);
    }
  };

  const inPreviewRange = (day) => {
    if (!draftFrom || draftTo) return false;
    const hover = hoverDate && hoverDate.getTime() >= draftFrom.getTime() ? hoverDate : draftFrom;
    return day.getTime() > draftFrom.getTime() && day.getTime() <= hover.getTime();
  };

  const isSelectedEdge = (day) => {
    return (draftFrom && day.getTime() === draftFrom.getTime()) ||
           (draftTo && day.getTime() === draftTo.getTime());
  };

  const isInFinalRange = (day) => {
    if (!draftFrom || !draftTo) return false;
    return day.getTime() > draftFrom.getTime() && day.getTime() < draftTo.getTime();
  };

  const changeMonth = (delta) => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
  };

  const handleApply = () => {
    onChange(draftFrom, draftTo);
    setOpen(false);
  };

  const handleClear = () => {
    setDraftFrom(null);
    setDraftTo(null);
    onChange(null, null);
    setOpen(false);
  };

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const cells = buildMonthGrid(year, month);

  const triggerText = draftFrom
    ? `${fmtDisplay(value?.from)}${value?.to ? '  →  ' + fmtDisplay(value?.to) : ''}`
    : label;

  return (
    <div className="drp-container" ref={containerRef}>
      <button
        type="button"
        className={`drp-trigger${value?.from ? ' drp-trigger-active' : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <FaCalendarAlt size={14} />
        <span className="drp-trigger-text">{value?.from ? triggerText : label}</span>
        {value?.from && (
          <span
            className="drp-trigger-clear"
            onClick={(e) => { e.stopPropagation(); onChange(null, null); }}
            aria-label="Clear date range"
          >
            <FaTimes size={11} />
          </span>
        )}
      </button>

      {open && (
        <div className="drp-popup">
          <div className="drp-popup-header">
            <button type="button" className="drp-nav-btn" onClick={() => changeMonth(-1)}>
              <FaChevronLeft size={12} />
            </button>
            <span className="drp-month-label">
              {viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
            </span>
            <button type="button" className="drp-nav-btn" onClick={() => changeMonth(1)}>
              <FaChevronRight size={12} />
            </button>
          </div>

          <div className="drp-weekday-row">
            {WEEKDAY_LABELS.map(w => <span key={w} className="drp-weekday">{w}</span>)}
          </div>

          <div className="drp-grid" onMouseLeave={() => setHoverDate(null)}>
            {cells.map((day, idx) => {
              if (!day) return <span key={idx} className="drp-cell drp-cell-empty" />;
              const selected = isSelectedEdge(day);
              const preview = inPreviewRange(day);
              const inRange = isInFinalRange(day);
              return (
                <button
                  key={idx}
                  type="button"
                  className={
                    'drp-cell drp-day' +
                    (selected ? ' drp-day-selected' : '') +
                    (inRange || preview ? ' drp-day-inrange' : '')
                  }
                  onMouseEnter={() => setHoverDate(day)}
                  onClick={() => handleDayClick(day)}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>

          <div className="drp-hint">
            {!draftFrom && 'Select a start date'}
            {draftFrom && !draftTo && 'Select an end date'}
            {draftFrom && draftTo && `${fmtDisplay(draftFrom)} – ${fmtDisplay(draftTo)}`}
          </div>

          <div className="drp-actions">
            <button type="button" className="drp-btn-secondary" onClick={handleClear}>Clear</button>
            <button
              type="button"
              className="drp-btn-primary"
              disabled={!draftFrom || !draftTo}
              onClick={handleApply}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
export { fmtISO, toDateOnly };